// src/Pages/Register/index.tsx

import React, { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress, useMediaQuery } from "@mui/material";
import {
  searchEstabilishments,
  checkEstabilishmentsTaken,
  postUserEstabilishment,
  checkEmailExists,
} from "../../Services/Estabilishment";
import type { RegisterPlan } from "../../Services/Estabilishment";
import { api } from "../../Services/Api";
import { getApiErrorMessage } from "../../Services/apiError";
import {
  PLACES_EMAIL,
  SUPPORT_WHATSAPP,
  SUPPORT_WHATSAPP_LABEL,
} from "../../Services/contacts";
import { useT } from "../../i18n";

// ─── Establishment Midware (map-based search) ─────────────────────────────────
const fetchEstabsMidware = async (
  userLat: number,
  userLng: number,
  mapLat: number,
  mapLng: number,
  maxDistance = 5,
  name = "",
): Promise<IEstabResult[]> => {
  const params = new URLSearchParams({
    userCoordinates: `${userLat} ${userLng}`,
    mapCoordinates: `${mapLat} ${mapLng}`,
    maxDistance: String(maxDistance),
    limit: "30",
  });
  if (name) params.append("criteria[estabilishment.name]", name);
  const res: any = await api.get(
    `/pt_BR/establishment_midware?${params.toString()}`,
  );
  return (res?.data?.data ?? []).map((e: any) => ({
    id: e.id,
    name: e.name,
    addressInGoogleFormat: e.addressInGoogleFormat ?? "",
    mapCoordinates: e.mapCoordinates ?? "",
  }));
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface IEstabResult {
  id: string;
  name: string;
  addressInGoogleFormat: string;
  mapCoordinates?: string;
}

type Step = 1 | 2 | 3 | 4 | "success";

// ─── Icons ────────────────────────────────────────────────────────────────────

const ic = (p: React.ReactNode) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {p}
  </svg>
);
const IconEye = () =>
  ic(
    <>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </>,
  );
const IconEyeOff = () =>
  ic(
    <>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </>,
  );
const IconSearch = () =>
  ic(
    <>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </>,
  );
const IconX = () =>
  ic(
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>,
  );
const IconStore = () =>
  ic(
    <>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </>,
  );
const IconUpload = () =>
  ic(
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </>,
  );
const IconFile = () =>
  ic(
    <>
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <polyline points="13 2 13 9 20 9" />
    </>,
  );

const IconWhatsApp = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35z" />
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 18.15h-.01c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.23 8.23z" />
  </svg>
);

/**
 * Suporte por WhatsApp — mesmo número usado no suporte do Connection.
 *
 * Fica fora dos blocos de passo, renderizado uma vez no rodapé do card, para
 * aparecer em todas as telas do cadastro (incluindo a de sucesso) sem repetir
 * o bloco em cada etapa. Preferi rodapé a botão flutuante porque o passo 2
 * tem o mapa com os controles de zoom no canto inferior direito.
 */
const SuporteWhatsApp = ({ passo }: { passo: Step }) => {
  const t = useT();

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        marginTop: 22,
        paddingTop: 16,
        borderTop: "1px solid #f0f0f0",
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontSize: 13, color: "#999" }}>
        {t("Precisa de ajuda?")}
      </span>
      <a
        href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(
          t(
            "Olá! Estou no cadastro do meu estabelecimento no Pertalks (passo {passo}) e preciso de ajuda.",
            { passo },
          ),
        )}`}
        target="_blank"
        rel="noreferrer"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          color: "#1a7a4a",
          background: "#e8f8ef",
          borderRadius: 999,
          padding: "6px 14px",
          fontSize: 13,
          fontWeight: 600,
          textDecoration: "none",
        }}
      >
        <IconWhatsApp />
        {SUPPORT_WHATSAPP_LABEL}
      </a>
    </div>
  );
};

const IconCheckLarge = () => (
  <svg
    width="44"
    height="44"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#00b6bc"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const stepDot = (active: boolean): React.CSSProperties => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: active ? "#00b6bc" : "#e0e0e0",
  transition: "background 0.2s",
  display: "inline-block",
  flexShrink: 0,
});

const fmtBytes = (b: number) =>
  b < 1024 * 1024
    ? `${(b / 1024).toFixed(0)} KB`
    : `${(b / 1024 / 1024).toFixed(1)} MB`;

// ─── Static styles ────────────────────────────────────────────────────────────

const page: React.CSSProperties = {
  minHeight: "100vh",
  background: "#f5f5f5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
};
const cardNarrow: React.CSSProperties = {
  background: "#fff",
  borderRadius: 18,
  padding: "36px 40px",
  width: "100%",
  maxWidth: 460,
  // sem border-box o padding soma aos 100% e o card vaza a tela no celular
  boxSizing: "border-box",
  boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
};
const cardWide: React.CSSProperties = {
  background: "#fff",
  borderRadius: 18,
  padding: "36px 40px",
  width: "100%",
  maxWidth: 900,
  boxSizing: "border-box",
  boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
};
const logo: React.CSSProperties = {
  display: "flex",
  justifyContent: "center",
  marginBottom: 28,
};
const steps: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 24,
};
const stepLine: React.CSSProperties = {
  flex: 1,
  height: 2,
  background: "#e0e0e0",
  borderRadius: 2,
};
const stepLineDone: React.CSSProperties = {
  flex: 1,
  height: 2,
  background: "#00b6bc",
  borderRadius: 2,
};
const stepLabel: React.CSSProperties = {
  fontSize: 12,
  color: "#888",
  marginBottom: 20,
};
const title: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 700,
  color: "#111",
  margin: "0 0 6px",
};
const subtitle: React.CSSProperties = {
  fontSize: 13,
  color: "#999",
  margin: "0 0 24px",
};
// minWidth 0 em toda a cadeia: sem isso, o <input> impõe sua largura
// intrínseca (~20 caracteres), a coluna do grid não encolhe abaixo disso e a
// linha inteira vaza para fora do card no celular.
const group: React.CSSProperties = { marginBottom: 18, minWidth: 0 };
const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#444",
  marginBottom: 6,
};
const required: React.CSSProperties = { color: "#e53935", marginLeft: 2 };
const inputWrap: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  border: "1.5px solid #e8e8e8",
  borderRadius: 10,
  padding: "0 12px",
  background: "#fff",
  boxSizing: "border-box",
  minWidth: 0,
  transition: "border-color .2s",
};
const inputStyleBase: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  width: "100%",
  border: "none",
  outline: "none",
  padding: "13px 0",
  fontSize: 14,
  color: "#222",
  background: "transparent",
};
const toggleBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "#aaa",
  display: "flex",
  alignItems: "center",
  padding: "0 0 0 8px",
};
const errorStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#e53935",
  marginTop: 4,
};
const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#aaa",
  marginTop: 4,
};
const actions: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 10,
  marginTop: 8,
};
const btnPrimary: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  padding: "12px 24px",
  background: "#00b6bc",
  color: "#fff",
  border: "none",
  borderRadius: 10,
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
};
const btnDisabled: React.CSSProperties = {
  ...btnPrimary,
  opacity: 0.5,
  cursor: "not-allowed",
};
const btnSecondary: React.CSSProperties = {
  padding: "12px 24px",
  background: "transparent",
  color: "#888",
  border: "1.5px solid #e0e0e0",
  borderRadius: 10,
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
};
const searchWrap: React.CSSProperties = { position: "relative" };
const results: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  background: "#fff",
  border: "1.5px solid #e8e8e8",
  borderRadius: 10,
  zIndex: 100,
  maxHeight: 220,
  overflowY: "auto",
  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
};
const resultItem: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  padding: "10px 14px",
  cursor: "pointer",
  borderBottom: "1px solid #f5f5f5",
};
const chips: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  marginTop: 12,
};
const chip: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "5px 12px",
  background: "#e6f9fa",
  borderRadius: 999,
  fontSize: 13,
  color: "#00b6bc",
  fontWeight: 500,
};
const chipRemove: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "#00b6bc",
  display: "flex",
  alignItems: "center",
  padding: 0,
};
const successBox: React.CSSProperties = {
  textAlign: "center",
  padding: "12px 0",
};
const loginLinkRow: React.CSSProperties = {
  color: "#888",
  fontSize: 13,
  textAlign: "center",
  marginTop: 20,
};
const loginLinkA: React.CSSProperties = {
  color: "#00b6bc",
  cursor: "pointer",
  fontWeight: 600,
  textDecoration: "none",
};

// ─── Drop zone styles (dynamic) ───────────────────────────────────────────────

const dropZone = (dragging: boolean): React.CSSProperties => ({
  border: `2px dashed ${dragging ? "#00b6bc" : "#e0e0e0"}`,
  borderRadius: 12,
  padding: "28px 20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  cursor: "pointer",
  transition: "border-color .2s, background .2s",
  background: dragging ? "#f0fbfc" : "#fafafa",
  textAlign: "center",
});

// ─── Planos oferecidos no cadastro ───────────────────────────────────────────

/** Reaproveita o tipo do serviço para não divergir do contrato da API */
/** E-mail de suporte para casos que exigem atendimento humano */
/** Mensagem única de e-mail inválido — usada no blur, no onChange e ao avançar */
const EMAIL_INVALID_MSG = "Digite um e-mail válido (ex.: nome@empresa.com.br).";

/**
 * Formato de e-mail: exatamente um @, sem espaços, e domínio com ponto.
 * Rejeita casos como "a@@b.com" e "a@b@c.com", que passavam antes.
 */
const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

// ─── Etapas ligadas/desligadas ───────────────────────────────────────────────

/**
 * Etapas de verificação de identidade: documentos do estabelecimento (passo 3)
 * e selfie com documento (passo 4).
 *
 * Desligadas por decisão de produto — o cadastro ficou curto para reduzir a
 * desistência no meio do caminho. O código das duas etapas continua inteiro
 * mais abaixo: voltar esta constante para `true` devolve o fluxo de 4 passos
 * sem reescrever nada.
 *
 * A API acompanha a mesma decisão: sem documento no cadastro, o vínculo nasce
 * aprovado, senão Banner e Push ficariam travados para sempre.
 */
const ETAPAS_DE_VERIFICACAO = false;

/**
 * Escolha de plano dentro do cadastro.
 *
 * Desligada: todo cadastro novo entra no Insight (3 meses grátis, sem cartão)
 * e o upgrade acontece dentro do painel, onde dá para comparar os planos com
 * calma. Quem decidia aqui tinha de decidir de novo lá depois.
 */
const ESCOLHA_DE_PLANO_NO_CADASTRO = false;

/** Passos visíveis, conforme as etapas ligadas acima. */
const TOTAL_PASSOS = ETAPAS_DE_VERIFICACAO ? 4 : 2;

// ─── CNPJ ────────────────────────────────────────────────────────────────────

const PESOS_CNPJ_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CNPJ_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/** Deixa só o que é CNPJ: dígitos e letras, no máximo 14. */
const limparCnpj = (valor: string) =>
  valor
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "")
    .slice(0, 14);

/** Máscara 00.000.000/0000-00 aplicada enquanto a pessoa digita. */
const formatarCnpj = (valor: string) => {
  const c = limparCnpj(valor);
  let saida = c.slice(0, 2);
  if (c.length > 2) saida += "." + c.slice(2, 5);
  if (c.length > 5) saida += "." + c.slice(5, 8);
  if (c.length > 8) saida += "/" + c.slice(8, 12);
  if (c.length > 12) saida += "-" + c.slice(12, 14);
  return saida;
};

const digitoCnpj = (base: number[], pesos: number[]) => {
  const soma = base.reduce((acc, v, i) => acc + v * pesos[i], 0);
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
};

/**
 * Validação de CNPJ pelo Módulo 11 da Receita Federal.
 *
 * Usa a regra do CNPJ alfanumérico (IN RFB 2.229/2024, em emissão desde
 * julho de 2026): cada caractere vale o código ASCII menos 48. Num CNPJ só de
 * dígitos essa conta devolve o próprio dígito, então a mesma função atende o
 * formato antigo e o novo. Validar apenas números recusaria CNPJ recém-emitido
 * e legítimo logo na porta de entrada do cadastro.
 */
const isValidCnpj = (valor: string) => {
  const c = limparCnpj(valor);
  if (c.length !== 14) return false;
  // os dois dígitos verificadores seguem numéricos mesmo no formato novo
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(c)) return false;
  // 00000000000000 e afins passam no módulo 11, mas não existem
  if (/^(.)\1{13}$/.test(c)) return false;

  const valores = c.split("").map((ch) => ch.charCodeAt(0) - 48);
  const base = valores.slice(0, 12);

  const dv1 = digitoCnpj(base, PESOS_CNPJ_DV1);
  if (dv1 !== valores[12]) return false;

  return digitoCnpj([...base, dv1], PESOS_CNPJ_DV2) === valores[13];
};

// ─── Vínculo com o estabelecimento ───────────────────────────────────────────

/**
 * O código é o que vai para o banco; o rótulo é só tela.
 *
 * Guardar o código e não o texto importa porque o painel tem português e
 * inglês: se gravássemos o rótulo, a mesma pessoa viraria "Proprietário" ou
 * "Owner" conforme o idioma em que preencheu o cadastro.
 */
const VINCULOS = [
  { codigo: "owner", rotulo: "Proprietário" },
  { codigo: "employee", rotulo: "Colaborador" },
  { codigo: "agency", rotulo: "Agência" },
] as const;

type VinculoCodigo = (typeof VINCULOS)[number]["codigo"] | "";

/** Proprietário se explica sozinho; nos outros dois perguntamos o quê. */
const precisaDetalharVinculo = (v: VinculoCodigo) =>
  v === "employee" || v === "agency";

/**
 * Aviso exibido nas DUAS formas de escolher o estabelecimento (busca e mapa):
 * quem não encontra o próprio local precisa de um caminho para pedir a
 * liberação da categoria, independente da aba em que está.
 */
const EstabNotFoundHint = () => {
  const t = useT();

  return (
    <div
      style={{
        background: "#e6f9fa",
        borderRadius: 10,
        padding: "14px 16px",
        marginBottom: 18,
        fontSize: 13,
        color: "#0b6f73",
        lineHeight: 1.7,
      }}
    >
      {t("Caso o seu estabelecimento não apareça no mapa Pertalks,")}{" "}
      <a
        href={`mailto:${PLACES_EMAIL}?subject=${encodeURIComponent(
          t("Liberar estabelecimento no Pertalks"),
        )}&body=${encodeURIComponent(
          t("Olá! Meu estabelecimento não aparece no mapa Pertalks.") +
            "\n\n" +
            t("Nome do estabelecimento:") +
            "\n" +
            t("Endereço completo:") +
            "\n\n" +
            t("Poderiam liberar a categoria no sistema?"),
        )}`}
        style={{
          color: "#0b6f73",
          textDecoration: "underline",
          fontWeight: 600,
        }}
      >
        {t("clique aqui")}
      </a>{" "}
      {t(
        "para nos enviar um email com o nome e endereço completo do seu estabelecimento para que possamos liberar a sua categoria em nosso sistema.",
      )}
    </div>
  );
};

type PlanKey = RegisterPlan;

interface PlanOption {
  key: PlanKey;
  name: string;
  /** Família do plano, quando houver (Pulse e Wave são Showcase) */
  family?: string;
  price: string;
  period: string;
  badge: string;
  badgeColor: string;
  badgeBg: string;
  features: string[];
}

const PLAN_OPTIONS: PlanOption[] = [
  {
    key: "Insight",
    name: "Insight",
    price: "Grátis",
    period: " por 3 meses",
    badge: "Sem cartão",
    badgeColor: "#0b6f73",
    badgeBg: "#cdf3f5",
    features: [
      "Total de check-ins",
      "Horários de pico",
      "Principais interesses do público",
      "Depois, R$ 29,90/mês",
    ],
  },
  {
    key: "Connection",
    name: "Connection",
    price: "R$ 67",
    period: "/mês",
    badge: "Mais completo",
    badgeColor: "#92400e",
    badgeBg: "#fef3c7",
    features: [
      "Tudo do Insight",
      "Notificações push",
      "Banners no app",
      "Destaque no mapa",
      "Suporte no WhatsApp",
    ],
  },
  {
    key: "Pulse",
    name: "Pulse",
    family: "Showcase",
    price: "R$ 199",
    // por tempo de veiculação (15 dias), não mensal — daí o sufixo vazio
    period: "",
    badge: "Divulgação",
    badgeColor: "#5b21b6",
    badgeBg: "#ede9fe",
    features: [
      "Banner na home do app",
      "Alcance de 10 km",
      "15 dias de veiculação",
      "Link direto para seu perfil",
    ],
  },
  {
    key: "Wave",
    name: "Wave",
    family: "Showcase",
    price: "R$ 399",
    // por tempo de veiculação (30 dias), não mensal — daí o sufixo vazio
    period: "",
    badge: "Maior alcance",
    badgeColor: "#5b21b6",
    badgeBg: "#ede9fe",
    features: [
      "Tudo do Pulse",
      "Alcance de 30 km",
      "30 dias de veiculação",
      "Prioridade na vitrine",
    ],
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export const Register = () => {
  const t = useT();
  const navigate = useNavigate();

  /**
   * Abaixo de 600px o formulário roda em uma coluna estreita: colunas fixas
   * lado a lado não cabem, e o que sobrava era o campo da direita saindo do
   * card (Número, Bairro e UF cortados).
   */
  const isMobile = useMediaQuery("(max-width:600px)");

  /**
   * Duas colunas que sabem encolher.
   *
   * `minmax(0, 1fr)` em vez de `1fr`: o padrão `1fr` é `minmax(auto, 1fr)` e
   * respeita a largura mínima do conteúdo — com um <input> dentro, isso
   * significa não encolher.
   */
  const grid2 = (
    colunasMobile: string,
    colunasDesktop: string,
    gap = 10,
  ): React.CSSProperties => ({
    display: "grid",
    gridTemplateColumns: isMobile ? colunasMobile : colunasDesktop,
    gap,
  });

  /**
   * No iOS, campo com fonte menor que 16px faz o Safari dar zoom sozinho ao
   * receber foco — a tela salta e o usuário precisa voltar na mão. No desktop
   * seguimos com 14px.
   */
  const inputStyle: React.CSSProperties = {
    ...inputStyleBase,
    fontSize: isMobile ? 16 : 14,
  };

  /**
   * Mesma moldura do inputWrap, só que aplicada direto no <select>.
   * O focusBorder/blurBorder dos inputs mexe no elemento PAI (é lá que mora
   * a borda), então não serve aqui — o select tem a borda em si mesmo.
   */
  const selectStyle: React.CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    border: "1.5px solid #e8e8e8",
    borderRadius: 10,
    padding: "13px 12px",
    fontSize: isMobile ? 16 : 14,
    color: "#222",
    background: "#fff",
    cursor: "pointer",
    outline: "none",
    transition: "border-color .2s",
  };

  const [step, setStep] = useState<Step>(1);

  // Step 1
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [emailChecking, setEmailChecking] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [step1Errors, setStep1Errors] = useState<Record<string, string>>({});
  // Vínculo da pessoa com o estabelecimento + detalhe (função ou agência)
  const [vinculo, setVinculo] = useState<VinculoCodigo>("");
  const [vinculoDetalhe, setVinculoDetalhe] = useState("");

  // Address fields
  const [postalCode, setPostalCode] = useState("");
  const [street, setStreet] = useState("");
  const [number, setNumber] = useState("");
  const [complement, setComplement] = useState("");
  const [district, setDistrict] = useState("");
  const [city, setCity] = useState("");
  const [uf, setUf] = useState("");
  const [cepLoading, setCepLoading] = useState(false);

  // Step 2
  const [cnpj, setCnpj] = useState("");
  const [step2Errors, setStep2Errors] = useState<Record<string, string>>({});
  const [instagram, setInstagram] = useState("");
  const [site, setSite] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState<IEstabResult[]>([]);
  // IDs de estabelecimentos já vinculados a outro usuário — avisamos na
  // escolha (passo 2), em vez de falhar só no envio final com 409
  const [takenIds, setTakenIds] = useState<Set<string>>(new Set());
  // Nome do estabelecimento já vinculado a outro usuário.
  // Guardamos só o nome (e não a frase pronta) para o aviso poder conter
  // um link clicável para o suporte.
  const [takenEstabName, setTakenEstabName] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [selected, setSelected] = useState<IEstabResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  // ── Map state (igual ao Maps.tsx do React Native) ────────────────────────
  const [selMode, setSelMode] = useState<"search" | "map">("search");
  const [mapEstabs, setMapEstabs] = useState<IEstabResult[]>([]);
  const [mapLoading, setMapLoading] = useState(false); // overlay de carregamento
  const [movLoading, setMovLoading] = useState(false); // botão "Pesquisar nesta área"
  const [userCoords, setUserCoords] = useState<[number, number]>([
    -23.55, -46.63,
  ]);
  const [mapCenter, setMapCenter] = useState<[number, number]>([
    -23.55, -46.63,
  ]);
  const [mapSearch, setMapSearch] = useState("");
  const [mapRadius, setMapRadius] = useState(5);
  const mapRef = useRef<any>(null); // div container do mapa
  const googleMapRef = useRef<any>(null); // instância google.maps.Map
  const markersRef = useRef<any[]>([]);
  const activeInfoWinRef = useRef<any>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  // Step 4 — selfie segurando documento pessoal
  const [selfie, setSelfie] = useState<File | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);

  // ── Captura por câmera ────────────────────────────────────────────────────
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  /** Encerra o stream — sem isso a luz da câmera continua acesa. */
  const stopCamera = React.useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOpen(false);
  }, []);

  const openCamera = async () => {
    setCameraError("");

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(
        "Seu navegador não permite usar a câmera aqui. Envie um arquivo pelo botão abaixo.",
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 960 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOpen(true);

      // o <video> só existe depois do render
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 0);
    } catch (err: any) {
      const nome = err?.name ?? "";
      if (nome === "NotAllowedError" || nome === "SecurityError") {
        setCameraError(
          "Precisamos da permissão de câmera. Libere o acesso nas configurações do navegador ou envie um arquivo.",
        );
      } else if (nome === "NotFoundError" || nome === "DevicesNotFoundError") {
        setCameraError(
          "Nenhuma câmera encontrada. Envie um arquivo pelo botão abaixo.",
        );
      } else {
        setCameraError(
          "Não foi possível abrir a câmera. Envie um arquivo pelo botão abaixo.",
        );
      }
      stopCamera();
    }
  };

  /** Congela o quadro atual do vídeo e transforma em arquivo. */
  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // espelha na horizontal para a foto sair como a pessoa se vê na tela
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `selfie-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        setSubmitError("");
        setSelfie(file);
        setSelfiePreview(URL.createObjectURL(file));
        stopCamera();
      },
      "image/jpeg",
      0.9,
    );
  };

  // garante o desligamento da câmera ao sair da tela
  useEffect(() => stopCamera, [stopCamera]);

  // Plano escolhido no cadastro
  const [plan, setPlan] = useState<PlanKey>("Insight");

  // resumo mostrado na tela de sucesso
  const [registeredPlan, setRegisteredPlan] = useState<{
    planName: string;
    trialEndsAt: string | null;
    requiresCard: boolean;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Submit
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [uploadProgress, setUploadProgress] = useState("");

  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Outside click ─────────────────────────────────────────────────────────

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node))
        setShowResults(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // ── Debounced search ──────────────────────────────────────────────────────

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchQ.length < 2) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setSearchLoading(true);
      searchEstabilishments(searchQ)
        .then(async (r: any) => {
          const results: IEstabResult[] = r?.data?.data ?? [];
          setSearchResults(results);
          setShowResults(true);

          // marca os que já têm dono para desabilitar na lista
          if (results.length > 0) {
            try {
              const chk: any = await checkEstabilishmentsTaken(
                results.map((x) => x.id),
              );
              const taken: string[] = chk?.data?.data?.taken ?? [];
              setTakenIds((prev) => new Set([...Array.from(prev), ...taken]));
            } catch (_) {
              // se a checagem falhar, o cadastro ainda valida no envio
            }
          }
        })
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 350);
  }, [searchQ]);

  // ── Load Google Maps JS API once ──────────────────────────────────────────
  useEffect(() => {
    const SCRIPT_ID = "google-maps-api-script";
    if (document.getElementById(SCRIPT_ID)) return; // já carregado ou carregando
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&loading=async&language=pt-BR`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  // ── addEstab definido antes do scanPin para evitar TDZ ───────────────────
  const addEstab = React.useCallback(
    async (e: IEstabResult) => {
      // já sabemos que tem dono
      if (takenIds.has(e.id)) return;

      // vindo do mapa, ainda não checamos: confirma antes de adicionar
      try {
        const chk: any = await checkEstabilishmentsTaken([e.id]);
        const taken: string[] = chk?.data?.data?.taken ?? [];
        if (taken.includes(e.id)) {
          setTakenIds((prev) => new Set([...Array.from(prev), e.id]));
          setTakenEstabName(e.name);
          return;
        }
      } catch (_) {
        // checagem indisponível: segue, o envio final ainda valida
      }

      setTakenEstabName("");
      setSelected((p) => (p.find((s) => s.id === e.id) ? p : [...p, e]));
      setSearchQ("");
      setShowResults(false);
    },
    [takenIds],
  ); // eslint-disable-line

  // ── scanPin — busca estabelecimentos (igual ao scanPin do Maps.tsx) ────────
  const scanPin = React.useCallback(
    (lat: number, lng: number, radius: number, search: string) => {
      setMapLoading(true);
      fetchEstabsMidware(userCoords[0], userCoords[1], lat, lng, radius, search)
        .then((estabs) => {
          setMapEstabs(estabs);
          setMovLoading(false);

          const G = (window as any).google;
          const map = googleMapRef.current;
          if (!G?.maps || !map) return;

          // Remove markers anteriores
          markersRef.current.forEach((m) => m.setMap(null));
          markersRef.current = [];

          // Callback global para botão no InfoWindow (igual ao handleMarkerPress)
          (window as any).__pertalksSelectEstab = (id: string) => {
            const estab = estabs.find((e) => e.id === id);
            if (estab) addEstab(estab);
            activeInfoWinRef.current?.close();
          };

          estabs.forEach((e) => {
            if (!e.mapCoordinates) return;
            const parts = e.mapCoordinates.split(" ").map(Number);
            const lat2 = parts[0],
              lng2 = parts[1];
            if (isNaN(lat2) || isNaN(lng2)) return;

            const marker = new G.maps.Marker({
              position: { lat: lat2, lng: lng2 },
              map,
              title: e.name,
              icon: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
                  <path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 26 18 26S36 31.5 36 18C36 8.06 27.94 0 18 0z" fill="#00b6bc"/>
                  <circle cx="18" cy="18" r="8" fill="#fff"/>
                </svg>`)}`,
                scaledSize: new G.maps.Size(36, 44),
                anchor: new G.maps.Point(18, 44),
              },
            });

            const infoWindow = new G.maps.InfoWindow({
              content: `
              <div style="font-family:Arial,sans-serif;min-width:190px;padding:6px 2px;">
                <strong style="font-size:13px;color:#111;">${e.name}</strong>
                ${
                  e.addressInGoogleFormat
                    ? `<p style="font-size:11px;color:#888;margin:4px 0 8px;line-height:1.4;">${e.addressInGoogleFormat}</p>`
                    : "<br/>"
                }
                <button onclick="window.__pertalksSelectEstab('${e.id}')"
                  style="width:100%;padding:7px 0;background:#00b6bc;color:#fff;border:none;
                         border-radius:7px;cursor:pointer;font-size:12px;font-weight:700;">
                  ${t("Selecionar")}
                </button>
              </div>`,
            });

            marker.addListener("click", () => {
              activeInfoWinRef.current?.close();
              infoWindow.open({ map, anchor: marker });
              activeInfoWinRef.current = infoWindow;
            });

            markersRef.current.push(marker);
          });
        })
        .catch(() => {
          setMovLoading(false);
        })
        .finally(() => setMapLoading(false));
    },
    // `t` fica fora das deps de propósito: trocar o idioma não deve refazer
    // a busca de estabelecimentos no mapa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userCoords, addEstab],
  ); // eslint-disable-line

  // ── Initialize Google Maps when switching to map mode ─────────────────────
  useEffect(() => {
    if (selMode !== "map" || !mapRef.current) return;

    const initMap = () => {
      const G = (window as any).google;
      if (!G?.maps || googleMapRef.current) return;

      const map = new G.maps.Map(mapRef.current, {
        center: { lat: userCoords[0], lng: userCoords[1] },
        zoom: 15,
        gestureHandling: "cooperative",
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
      });

      googleMapRef.current = map;

      // Debounce 300ms no regionChange — igual ao Maps.tsx
      map.addListener("idle", () => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          const c = map.getCenter();
          if (!c) return; // guard: mapa ainda inicializando
          setMapCenter([c.lat(), c.lng()]);
          setMovLoading(true); // mostra botão "Pesquisar nesta área"
        }, 300);
      });

      // Geolocalização inicial
      navigator.geolocation?.getCurrentPosition(
        (pos) => {
          const coords: [number, number] = [
            pos.coords.latitude,
            pos.coords.longitude,
          ];
          setUserCoords(coords);
          setMapCenter(coords);
          map.setCenter({ lat: coords[0], lng: coords[1] });
          scanPin(coords[0], coords[1], mapRadius, mapSearch);
        },
        () => {
          scanPin(userCoords[0], userCoords[1], mapRadius, mapSearch);
        },
      );
    };

    if ((window as any).google?.maps) {
      setTimeout(initMap, 50);
    } else {
      const interval = setInterval(() => {
        if ((window as any).google?.maps) {
          clearInterval(interval);
          setTimeout(initMap, 50);
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [selMode]); // eslint-disable-line

  // ── Verificação inline de e-mail já cadastrado ────────────────────────────

  const handleEmailBlur = async (ev?: React.FocusEvent<HTMLInputElement>) => {
    if (ev) blurBorder(ev);

    const value = email.trim();

    // Campo vazio: não acusa erro ainda — a validação obrigatória fica
    // para o momento de avançar a etapa.
    if (!value) {
      setStep1Errors((prev) => {
        const next = { ...prev };
        if (next.email === EMAIL_INVALID_MSG) delete next.email;
        return next;
      });
      return;
    }

    // Formato inválido: avisa NA HORA, abaixo do campo.
    // Antes esse caso saía em silêncio e o usuário só descobria o problema
    // ao enviar o cadastro, na última etapa.
    if (!isValidEmail(value)) {
      setStep1Errors((prev) => ({ ...prev, email: EMAIL_INVALID_MSG }));
      return;
    }

    // formato ok: limpa erro de formato antes de consultar a API
    setStep1Errors((prev) => {
      const next = { ...prev };
      if (next.email === EMAIL_INVALID_MSG) delete next.email;
      return next;
    });

    setEmailChecking(true);
    try {
      const res: any = await checkEmailExists(value);
      if (res?.data?.data?.exists) {
        setStep1Errors((prev) => ({
          ...prev,
          email: "Este e-mail já está sendo usado.",
        }));
      } else {
        // limpa só o erro de e-mail se ele era o de duplicidade
        setStep1Errors((prev) => {
          const next = { ...prev };
          if (next.email === "Este e-mail já está sendo usado.") {
            delete next.email;
          }
          return next;
        });
      }
    } catch (_) {
      // silencioso — se a checagem falhar, o backend ainda valida no envio
    } finally {
      setEmailChecking(false);
    }
  };

  // ── Step 1 validation ─────────────────────────────────────────────────────

  const validateStep1 = () => {
    const e: Record<string, string> = {};
    if (!fullName.trim()) e.fullName = "Informe seu nome completo.";
    // Vazio E formato inválido — sem a segunda checagem o cadastro avançava
    // com e-mail malformado e só quebrava no envio final.
    if (!email.trim()) {
      e.email = "Informe seu e-mail.";
    } else if (!isValidEmail(email)) {
      e.email = EMAIL_INVALID_MSG;
    }
    if (password.length < 8)
      e.password = "A senha deve ter no mínimo 8 caracteres.";
    if (password !== confirmPassword) e.confirm = "As senhas não coincidem.";
    if (!vinculo) {
      e.vinculo = "Escolha seu vínculo com o estabelecimento.";
    } else if (precisaDetalharVinculo(vinculo) && !vinculoDetalhe.trim()) {
      e.vinculoDetalhe =
        vinculo === "agency"
          ? "Informe o nome da agência."
          : "Informe sua função no estabelecimento.";
    }
    if (!postalCode.replace(/\D/g, "")) e.postalCode = "Informe o CEP.";
    if (!street.trim()) e.street = "Informe o endereço.";
    if (!number.trim()) e.number = "Informe o número.";
    if (!district.trim()) e.district = "Informe o bairro.";
    if (!city.trim()) e.city = "Informe a cidade.";
    if (!uf.trim()) e.uf = "Informe o estado (UF).";
    setStep1Errors(e);
    return Object.keys(e).length === 0;
  };

  // ── Step 2 validation ─────────────────────────────────────────────────────

  const validateStep2 = () => {
    const e: Record<string, string> = {};

    if (!limparCnpj(cnpj)) {
      e.cnpj = "Informe o CNPJ do estabelecimento.";
    } else if (!isValidCnpj(cnpj)) {
      e.cnpj = "CNPJ inválido. Confira o número digitado.";
    }

    if (selected.length === 0) {
      e.estabilishments = "Selecione ao menos um estabelecimento.";
    }

    setStep2Errors(e);
    return Object.keys(e).length === 0;
  };

  // ── Establishment helpers ─────────────────────────────────────────────────

  const removeEstab = (id: string) =>
    setSelected((p) => p.filter((s) => s.id !== id));

  // ── File helpers ──────────────────────────────────────────────────────────

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const arr = Array.from(incoming);
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...arr.filter((f) => !existing.has(f.name + f.size))];
    });
  };

  const removeFile = (idx: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  // ── Final submit (register → upload) ────────────────────────────────────

  const handleFinalSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Só cobra arquivo quando as etapas de verificação estão ligadas; com
    // elas desligadas não existe passo 3 nem 4 para a pessoa preencher.
    if (ETAPAS_DE_VERIFICACAO) {
      if (files.length === 0) {
        setSubmitError("Envie ao menos um documento.");
        return;
      }
      if (!selfie) {
        setSubmitError("Envie a selfie segurando seu documento.");
        return;
      }
    }

    setSubmitError("");
    setSubmitLoading(true);

    try {
      // Cadastro e documentos num request só. Se qualquer parte falhar, o
      // backend faz rollback e NADA é criado — então o botão pode ser
      // apertado de novo sem esbarrar em "e-mail já cadastrado".
      setUploadProgress(
        ETAPAS_DE_VERIFICACAO
          ? "Enviando cadastro e documentos..."
          : "Criando sua conta...",
      );
      const regRes: any = await postUserEstabilishment(
        {
          email,
          password,
          plan,
          relationship: vinculo || null,
          relationshipDetail: vinculoDetalhe.trim() || null,
          // sem máscara: o banco guarda só os 14 caracteres
          cnpj: limparCnpj(cnpj) || null,
          profile: {
            fullName: fullName.trim(),
            instagram: instagram.trim() || null,
            site: site.trim() || null,
            address: {
              postalCode: postalCode.replace(/\D/g, ""),
              street: street.trim(),
              number: number.trim(),
              complement: complement.trim(),
              district: district.trim(),
              city: city.trim(),
              state: uf.trim(),
              stateShort: uf.trim(),
              country: "Brasil",
            },
          },
          estabilishments: selected.map((s) => s.id),
        },
        ETAPAS_DE_VERIFICACAO ? files : [],
        ETAPAS_DE_VERIFICACAO ? selfie : null,
      );

      const regData = regRes?.data?.data ?? {};

      // Connection exige cartão. O checkout da Stripe é autenticado e o
      // usuário ainda não tem token aqui, então registramos a intenção:
      // ao fazer login, o app leva direto para a área de Assinatura.
      if (regData.requiresCard) {
        localStorage.setItem(
          "postLoginRedirect",
          "/establishment/subscription",
        );
      }

      setRegisteredPlan({
        planName: regData.planName ?? plan,
        trialEndsAt: regData.trialEndsAt ?? null,
        requiresCard: !!regData.requiresCard,
      });
      setStep("success");
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setSubmitError(
          "Um dos estabelecimentos já está vinculado a outro usuário.",
        );
      } else if (err?.response?.status === 413) {
        setSubmitError(
          "Os arquivos somados ficaram grandes demais para o envio. " +
            "Remova algum documento ou envie fotos menores e tente de novo.",
        );
      } else {
        setSubmitError(
          getApiErrorMessage(err, "Ocorreu um erro. Tente novamente."),
        );
      }
    } finally {
      setSubmitLoading(false);
      setUploadProgress("");
    }
  };

  // ── CEP auto-fill (ViaCEP) ────────────────────────────────────────────────
  const handleCepBlur = async () => {
    const cep = postalCode.replace(/\D/g, "");
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await res.json();
      if (!d.erro) {
        setStreet(d.logradouro ?? "");
        setDistrict(d.bairro ?? "");
        setCity(d.localidade ?? "");
        setUf(d.uf ?? "");
      }
    } catch (_) {
      /* silencioso */
    } finally {
      setCepLoading(false);
    }
  };

  // ── Format CEP mask ───────────────────────────────────────────────────────
  const maskCep = (v: string) => {
    const d = v.replace(/\D/g, "").slice(0, 8);
    return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
  };

  // ── Focus helpers ─────────────────────────────────────────────────────────

  const focusBorder = (e: React.FocusEvent<HTMLInputElement>) => {
    const p = e.currentTarget.parentElement;
    if (p) p.style.borderColor = "#00b6bc";
  };
  const blurBorder = (e: React.FocusEvent<HTMLInputElement>) => {
    const p = e.currentTarget.parentElement;
    if (p) p.style.borderColor = "#e8e8e8";
  };

  // ── Step indicator ────────────────────────────────────────────────────────

  /**
   * Bolinhas montadas a partir de TOTAL_PASSOS: com as etapas de verificação
   * desligadas o indicador mostra 2, e não 4 com duas que nunca acendem.
   */
  const StepIndicator = ({ current }: { current: 1 | 2 | 3 | 4 }) => (
    <>
      <div style={steps}>
        {Array.from({ length: TOTAL_PASSOS }, (_, i) => i + 1).map((n) => (
          <React.Fragment key={n}>
            {n > 1 && <div style={current >= n ? stepLineDone : stepLine} />}
            <span style={stepDot(current >= n)} />
          </React.Fragment>
        ))}
      </div>
      <p style={stepLabel}>
        {t("Passo {n} de {total}", { n: current, total: TOTAL_PASSOS })}
      </p>
    </>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ ...page, padding: isMobile ? 12 : 20 }}>
      <div
        style={{
          ...(step === 2 ? cardWide : cardNarrow),
          padding: isMobile ? "24px 18px" : "36px 40px",
        }}
      >
        <div style={logo}>
          <img src="/Pertalks.svg" alt="Pertalks" style={{ height: 32 }} />
        </div>

        {/* ── SUCCESS ─────────────────────────────────────────────────── */}
        {step === "success" && (
          <div style={successBox}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 16,
              }}
            >
              <IconCheckLarge />
            </div>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: "#111",
                margin: "0 0 10px",
              }}
            >
              {t("Tudo pronto! Seu painel já está liberado")}
            </h2>
            <p
              style={{
                fontSize: 14,
                color: "#888",
                lineHeight: 1.6,
                margin: "0 0 18px",
              }}
            >
              {ETAPAS_DE_VERIFICACAO ? (
                <>
                  {t("Seus documentos serão analisados em até")}{" "}
                  <strong>{t("72 horas")}</strong>
                  {t(
                    ", mas você não precisa esperar — pode entrar e começar a usar agora.",
                  )}
                </>
              ) : (
                t(
                  "Sua conta já está ativa. É só entrar e começar a acompanhar os check-ins do seu estabelecimento.",
                )
              )}
            </p>

            {registeredPlan?.trialEndsAt && (
              <div
                style={{
                  background: "#e6f9fa",
                  borderRadius: 10,
                  padding: "12px 14px",
                  marginBottom: 18,
                  fontSize: 13,
                  color: "#0b6f73",
                  lineHeight: 1.6,
                }}
              >
                {t("Plano")} <strong>{registeredPlan.planName}</strong>{" "}
                {t("liberado sem custo até")}{" "}
                <strong>
                  {new Date(registeredPlan.trialEndsAt).toLocaleDateString(
                    "pt-BR",
                  )}
                </strong>
                .
                {registeredPlan.requiresCard
                  ? ` ${t("A cobrança no seu cartão começa a partir dessa data.")}`
                  : ` ${t("Avisaremos por e-mail antes do fim do período.")}`}
              </div>
            )}

            {registeredPlan?.requiresCard && (
              <div
                style={{
                  background: "#fff7e6",
                  borderRadius: 10,
                  padding: "12px 14px",
                  marginBottom: 18,
                  fontSize: 13,
                  color: "#92400e",
                  lineHeight: 1.6,
                }}
              >
                {plan === "Connection" ? (
                  <>
                    {t(
                      "Ao entrar, você será redirecionado para concluir a assinatura do plano escolhido.",
                    )}
                  </>
                ) : (
                  <>
                    <strong>{t("Falta cadastrar seu cartão.")}</strong>{" "}
                    {t(
                      "Ao entrar, você será levado direto para a área de Assinatura para concluir a assinatura do plano {plano}.",
                      { plano: registeredPlan?.planName ?? plan },
                    )}
                  </>
                )}
              </div>
            )}

            {/* Banner e Push existem apenas no Connection, e só abrem após a
                aprovação dos documentos. Insight não tem essas áreas e
                Pulse/Wave usam o fluxo de aprovação da arte. */}
            {ETAPAS_DE_VERIFICACAO && plan === "Connection" && (
              <p
                style={{
                  fontSize: 13,
                  color: "#888",
                  lineHeight: 1.6,
                  margin: "0 0 18px",
                }}
              >
                {t("As áreas de")} <strong>{t("Banners")}</strong> {t("e")}{" "}
                <strong>{t("Notificações Push")}</strong>{" "}
                {t("são liberadas após a aprovação dos documentos.")}
              </p>
            )}

            <button
              style={{ ...btnPrimary, width: "100%" }}
              onClick={() => navigate("/login")}
            >
              {registeredPlan?.requiresCard
                ? t("Entrar e cadastrar cartão")
                : t("Acessar meu painel")}
            </button>
          </div>
        )}

        {/* ── STEP 1: Dados pessoais ──────────────────────────────────── */}
        {step === 1 && (
          <>
            <StepIndicator current={1} />
            <h2 style={title}>{t("Crie sua conta")}</h2>
            <p style={subtitle}>
              {t("Dados pessoais do responsável pelo estabelecimento.")}
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                // não avança com e-mail duplicado nem com formato inválido
                if (
                  step1Errors.email === "Este e-mail já está sendo usado." ||
                  step1Errors.email === EMAIL_INVALID_MSG
                )
                  return;
                if (validateStep1()) setStep(2);
              }}
              noValidate
            >
              <div style={group}>
                <label style={labelStyle}>
                  {t("Nome completo")} <span style={required}>*</span>
                </label>
                <div style={inputWrap}>
                  <input
                    style={inputStyle}
                    type="text"
                    placeholder={t("Seu nome completo")}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    onFocus={focusBorder}
                    onBlur={blurBorder}
                  />
                </div>
                {step1Errors.fullName && (
                  <p style={errorStyle}>{t(step1Errors.fullName)}</p>
                )}
              </div>

              <div style={group}>
                <label style={labelStyle}>
                  {t("E-mail")} <span style={required}>*</span>
                </label>
                <div style={inputWrap}>
                  <input
                    style={inputStyle}
                    type="email"
                    placeholder={t("seu@email.com")}
                    value={email}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEmail(v);

                      // Some com o aviso assim que o formato fica válido,
                      // sem esperar o campo perder o foco.
                      if (step1Errors.email === EMAIL_INVALID_MSG && isValidEmail(v)) {
                        setStep1Errors((prev) => {
                          const next = { ...prev };
                          delete next.email;
                          return next;
                        });
                      }
                    }}
                    onFocus={focusBorder}
                    onBlur={handleEmailBlur}
                  />
                  {emailChecking && (
                    <CircularProgress
                      size={14}
                      style={{ color: "#00b6bc", marginLeft: 8, flexShrink: 0 }}
                    />
                  )}
                </div>
                {step1Errors.email && (
                  <p style={errorStyle}>{t(step1Errors.email)}</p>
                )}
              </div>

              <div style={group}>
                <label style={labelStyle}>
                  {t("Senha")} <span style={required}>*</span>
                </label>
                <div style={inputWrap}>
                  <input
                    style={inputStyle}
                    type={showPass ? "text" : "password"}
                    placeholder={t("Mínimo 8 caracteres")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={focusBorder}
                    onBlur={blurBorder}
                    minLength={8}
                    maxLength={30}
                  />
                  <button
                    type="button"
                    style={toggleBtn}
                    onClick={() => setShowPass((v) => !v)}
                  >
                    {showPass ? <IconEyeOff /> : <IconEye />}
                  </button>
                </div>
                {step1Errors.password && (
                  <p style={errorStyle}>{t(step1Errors.password)}</p>
                )}
              </div>

              <div style={group}>
                <label style={labelStyle}>
                  {t("Confirmar senha")} <span style={required}>*</span>
                </label>
                <div style={inputWrap}>
                  <input
                    style={inputStyle}
                    type={showConfirm ? "text" : "password"}
                    placeholder={t("Repita a senha")}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={focusBorder}
                    onBlur={blurBorder}
                    minLength={8}
                    maxLength={30}
                  />
                  <button
                    type="button"
                    style={toggleBtn}
                    onClick={() => setShowConfirm((v) => !v)}
                  >
                    {showConfirm ? <IconEyeOff /> : <IconEye />}
                  </button>
                </div>
                {step1Errors.confirm && (
                  <p style={errorStyle}>{t(step1Errors.confirm)}</p>
                )}
              </div>

              {/* ── Vínculo com o estabelecimento ───────────────────────────
                  Substitui a conferência de documentos: em vez de provar quem
                  é com papel, a pessoa declara o vínculo e a equipe usa isso
                  na hora de falar com ela. */}
              <div style={group}>
                <label style={labelStyle}>
                  {t("Seu vínculo com o estabelecimento")}{" "}
                  <span style={required}>*</span>
                </label>
                <select
                  style={selectStyle}
                  value={vinculo}
                  onChange={(ev) => {
                    setVinculo(ev.target.value as VinculoCodigo);
                    // trocar de Colaborador para Agência carregando o texto
                    // antigo deixaria "Gerente" no campo "Nome da agência"
                    setVinculoDetalhe("");
                    setStep1Errors((prev) => {
                      const next = { ...prev };
                      delete next.vinculo;
                      delete next.vinculoDetalhe;
                      return next;
                    });
                  }}
                  onFocus={(ev) => {
                    ev.currentTarget.style.borderColor = "#00b6bc";
                  }}
                  onBlur={(ev) => {
                    ev.currentTarget.style.borderColor = "#e8e8e8";
                  }}
                >
                  <option value="">{t("Selecione")}</option>
                  {VINCULOS.map((v) => (
                    <option key={v.codigo} value={v.codigo}>
                      {t(v.rotulo)}
                    </option>
                  ))}
                </select>
                {step1Errors.vinculo && (
                  <p style={errorStyle}>{t(step1Errors.vinculo)}</p>
                )}
              </div>

              {precisaDetalharVinculo(vinculo) && (
                <div style={group}>
                  <label style={labelStyle}>
                    {vinculo === "agency"
                      ? t("Nome da agência")
                      : t("Sua função no estabelecimento")}{" "}
                    <span style={required}>*</span>
                  </label>
                  <div style={inputWrap}>
                    <input
                      style={inputStyle}
                      type="text"
                      placeholder={
                        vinculo === "agency"
                          ? t("Ex.: Agência Criativa")
                          : t("Ex.: Gerente de marketing")
                      }
                      value={vinculoDetalhe}
                      onChange={(ev) => setVinculoDetalhe(ev.target.value)}
                      onFocus={focusBorder}
                      onBlur={blurBorder}
                      maxLength={100}
                    />
                  </div>
                  {step1Errors.vinculoDetalhe && (
                    <p style={errorStyle}>{t(step1Errors.vinculoDetalhe)}</p>
                  )}
                </div>
              )}

              {/* ── Endereço ──────────────────────────────────────────────── */}
              <div
                style={{
                  borderTop: "1px solid #f0f0f0",
                  margin: "20px 0 16px",
                }}
              />
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#333",
                  margin: "0 0 14px",
                }}
              >
                {t("Endereço")}
              </p>

              {/* CEP */}
              <div style={group}>
                <label style={labelStyle}>
                  {t("CEP")} <span style={required}>*</span>
                </label>
                <div style={inputWrap}>
                  <input
                    style={inputStyle}
                    type="text"
                    placeholder="00000-000"
                    value={postalCode}
                    onChange={(e) => setPostalCode(maskCep(e.target.value))}
                    onBlur={handleCepBlur}
                    maxLength={9}
                    inputMode="numeric"
                    onFocus={focusBorder}
                  />
                  {cepLoading && (
                    <CircularProgress
                      size={14}
                      style={{ color: "#00b6bc", flexShrink: 0 }}
                    />
                  )}
                </div>
                {step1Errors.postalCode && (
                  <p style={errorStyle}>{t(step1Errors.postalCode)}</p>
                )}
              </div>

              {/* Endereço + Número */}
              <div style={grid2("minmax(0, 1fr) 84px", "minmax(0, 1fr) 100px")}>
                <div style={group}>
                  <label style={labelStyle}>
                    {t("Endereço")} <span style={required}>*</span>
                  </label>
                  <div style={inputWrap}>
                    <input
                      style={inputStyle}
                      type="text"
                      placeholder={t("Rua, Av...")}
                      value={street}
                      onChange={(e) => setStreet(e.target.value)}
                      onFocus={focusBorder}
                      onBlur={blurBorder}
                    />
                  </div>
                  {step1Errors.street && (
                    <p style={errorStyle}>{t(step1Errors.street)}</p>
                  )}
                </div>
                <div style={group}>
                  <label style={labelStyle}>
                    {t("Número")} <span style={required}>*</span>
                  </label>
                  <div style={inputWrap}>
                    <input
                      style={inputStyle}
                      type="text"
                      placeholder={t("Nº")}
                      value={number}
                      onChange={(e) => setNumber(e.target.value)}
                      onFocus={focusBorder}
                      onBlur={blurBorder}
                    />
                  </div>
                  {step1Errors.number && (
                    <p style={errorStyle}>{t(step1Errors.number)}</p>
                  )}
                </div>
              </div>

              {/* Complemento + Bairro */}
              <div
                style={grid2("1fr", "minmax(0, 1fr) minmax(0, 1fr)")}
              >
                <div style={group}>
                  <label style={labelStyle}>{t("Complemento")}</label>
                  <div style={inputWrap}>
                    <input
                      style={inputStyle}
                      type="text"
                      placeholder={t("Apto, Sala...")}
                      value={complement}
                      onChange={(e) => setComplement(e.target.value)}
                      onFocus={focusBorder}
                      onBlur={blurBorder}
                    />
                  </div>
                </div>
                <div style={group}>
                  <label style={labelStyle}>
                    {t("Bairro")} <span style={required}>*</span>
                  </label>
                  <div style={inputWrap}>
                    <input
                      style={inputStyle}
                      type="text"
                      placeholder={t("Bairro")}
                      value={district}
                      onChange={(e) => setDistrict(e.target.value)}
                      onFocus={focusBorder}
                      onBlur={blurBorder}
                    />
                  </div>
                  {step1Errors.district && (
                    <p style={errorStyle}>{t(step1Errors.district)}</p>
                  )}
                </div>
              </div>

              {/* Cidade + UF */}
              <div style={grid2("minmax(0, 1fr) 68px", "minmax(0, 1fr) 80px")}>
                <div style={group}>
                  <label style={labelStyle}>
                    {t("Cidade")} <span style={required}>*</span>
                  </label>
                  <div style={inputWrap}>
                    <input
                      style={inputStyle}
                      type="text"
                      placeholder={t("Cidade")}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      onFocus={focusBorder}
                      onBlur={blurBorder}
                    />
                  </div>
                  {step1Errors.city && (
                    <p style={errorStyle}>{t(step1Errors.city)}</p>
                  )}
                </div>
                <div style={group}>
                  <label style={labelStyle}>
                    {t("UF")} <span style={required}>*</span>
                  </label>
                  <div style={inputWrap}>
                    <input
                      style={inputStyle}
                      type="text"
                      placeholder="SP"
                      value={uf}
                      onChange={(e) => setUf(e.target.value.toUpperCase())}
                      onFocus={focusBorder}
                      onBlur={blurBorder}
                      maxLength={2}
                    />
                  </div>
                  {step1Errors.uf && (
                    <p style={errorStyle}>{t(step1Errors.uf)}</p>
                  )}
                </div>
              </div>

              <div style={{ ...actions, justifyContent: "flex-end" }}>
                <button type="submit" style={btnPrimary}>
                  {t("Próximo")} →
                </button>
              </div>
            </form>

            <p style={loginLinkRow}>
              {t("Já tem conta?")}{" "}
              <span style={loginLinkA} onClick={() => navigate("/login")}>
                {t("Entrar")}
              </span>
            </p>
          </>
        )}

        {/* ── STEP 2: Estabelecimento ─────────────────────────────────── */}
        {step === 2 && (
          <>
            <StepIndicator current={2} />
            <h2 style={title}>{t("Seu estabelecimento")}</h2>
            <p style={subtitle}>
              {t(
                "Busque e selecione o(s) estabelecimento(s) que você gerencia.",
              )}
            </p>

            {/* ── CNPJ ─────────────────────────────────────────────────────
                Primeira coisa da tela, antes mesmo da busca: é o dado que
                liga a pessoa à empresa agora que não pedimos mais o cartão
                CNPJ em arquivo. */}
            <div style={group}>
              <label style={labelStyle}>
                {t("CNPJ do estabelecimento")} <span style={required}>*</span>
              </label>
              <div style={inputWrap}>
                <input
                  style={inputStyle}
                  type="text"
                  inputMode="text"
                  placeholder="00.000.000/0000-00"
                  value={cnpj}
                  onChange={(ev) => {
                    setCnpj(formatarCnpj(ev.target.value));
                    setStep2Errors((prev) => {
                      const next = { ...prev };
                      delete next.cnpj;
                      return next;
                    });
                  }}
                  onFocus={focusBorder}
                  onBlur={(ev) => {
                    blurBorder(ev);
                    // avisa na hora, e não só ao tentar concluir o cadastro
                    const valor = cnpj.trim();
                    if (valor && !isValidCnpj(valor)) {
                      setStep2Errors((prev) => ({
                        ...prev,
                        cnpj: "CNPJ inválido. Confira o número digitado.",
                      }));
                    }
                  }}
                  maxLength={18}
                />
              </div>
              {step2Errors.cnpj && (
                <p style={errorStyle}>{t(step2Errors.cnpj)}</p>
              )}
            </div>

            {/* Mode tabs */}
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 20,
                flexWrap: "wrap",
              }}
            >
              {(["search", "map"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setSelMode(mode)}
                  style={{
                    padding: "7px 18px",
                    border: "1.5px solid",
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    borderColor: selMode === mode ? "#00b6bc" : "#e0e0e0",
                    background: selMode === mode ? "#00b6bc" : "#fff",
                    color: selMode === mode ? "#fff" : "#666",
                  }}
                >
                  {mode === "search"
                    ? t("Buscar por nome")
                    : t("Selecionar no mapa")}
                </button>
              ))}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!validateStep2()) return;

                if (ETAPAS_DE_VERIFICACAO) {
                  setStep(3);
                  return;
                }

                // sem documentos e sem selfie, o passo 2 é o fim do cadastro
                handleFinalSubmit(e);
              }}
              noValidate
            >
              {/* ── Search tab ─────────────────────────────────────────── */}
              {selMode === "search" && (
                <div style={group}>
                  <label style={labelStyle}>
                    {t("Buscar estabelecimento")}{" "}
                    <span style={required}>*</span>
                  </label>
                  <div style={searchWrap} ref={searchRef}>
                    <div style={inputWrap}>
                      <span
                        style={{
                          color: "#aaa",
                          marginRight: 8,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {searchLoading ? (
                          <CircularProgress
                            size={14}
                            style={{ color: "#00b6bc" }}
                          />
                        ) : (
                          <IconSearch />
                        )}
                      </span>
                      <input
                        style={inputStyle}
                        type="text"
                        placeholder={t("Digite o nome do estabelecimento")}
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        onFocus={(e) => {
                          focusBorder(e);
                          if (searchResults.length) setShowResults(true);
                        }}
                        onBlur={blurBorder}
                        autoComplete="off"
                      />
                    </div>
                    {showResults && searchResults.length > 0 && (
                      <div style={results}>
                        {searchResults.map((r) => {
                          // já vinculado a outro usuário: exibe, mas não deixa escolher
                          const taken = takenIds.has(r.id);
                          return (
                            <div
                              key={r.id}
                              style={{
                                ...resultItem,
                                cursor: taken ? "not-allowed" : "pointer",
                                opacity: taken ? 0.55 : 1,
                              }}
                              onMouseDown={() => {
                                if (!taken) addEstab(r);
                              }}
                              onMouseEnter={(e) => {
                                if (!taken)
                                  e.currentTarget.style.background = "#f9f9f9";
                              }}
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "#fff")
                              }
                            >
                              <span
                                style={{
                                  color: taken ? "#bbb" : "#00b6bc",
                                  flexShrink: 0,
                                  marginTop: 1,
                                }}
                              >
                                <IconStore />
                              </span>
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 600,
                                      color: taken ? "#888" : "#111",
                                    }}
                                  >
                                    {r.name}
                                  </span>
                                  {taken && (
                                    <span
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        color: "#b45309",
                                        background: "#fff7e6",
                                        borderRadius: 999,
                                        padding: "2px 8px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {t("Já cadastrado")}
                                    </span>
                                  )}
                                </div>
                                {r.addressInGoogleFormat && (
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: "#999",
                                      marginTop: 2,
                                    }}
                                  >
                                    {r.addressInGoogleFormat}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {showResults &&
                      searchQ.length >= 2 &&
                      searchResults.length === 0 &&
                      !searchLoading && (
                        <div
                          style={{
                            ...results,
                            padding: "14px 16px",
                            fontSize: 13,
                            color: "#aaa",
                          }}
                        >
                          {t('Nenhum resultado para "{termo}".', {
                            termo: searchQ,
                          })}
                        </div>
                      )}
                  </div>
                  {/* marginBottom local para separar do box de aviso abaixo,
                      sem alterar o hintStyle usado em outros campos */}
                  <p style={{ ...hintStyle, marginBottom: 16 }}>
                    {t("Digite ao menos 2 caracteres para buscar.")}
                  </p>

                  <EstabNotFoundHint />
                </div>
              )}

              {/* ── Map tab ────────────────────────────────────────────── */}
              {selMode === "map" && (
                <div style={group}>
                  <label style={labelStyle}>
                    {t(
                      "Confirme o nome e endereço do estabelecimento no mapa",
                    )}{" "}
                    <span style={required}>*</span>
                  </label>

                  {/* Map search box */}
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      marginBottom: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ ...inputWrap, flex: 1 }}>
                      <span
                        style={{
                          color: "#aaa",
                          marginRight: 8,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {mapLoading ? (
                          <CircularProgress
                            size={14}
                            style={{ color: "#00b6bc" }}
                          />
                        ) : (
                          <IconSearch />
                        )}
                      </span>
                      <input
                        style={inputStyle}
                        type="text"
                        placeholder={t(
                          "Pesquise pelo endereço ou nome do estabelecimento",
                        )}
                        value={mapSearch}
                        onChange={(e) => setMapSearch(e.target.value)}
                      />
                    </div>
                    <select
                      value={mapRadius}
                      onChange={(e) => setMapRadius(Number(e.target.value))}
                      style={{
                        padding: "9px 12px",
                        border: "1.5px solid #e8e8e8",
                        borderRadius: 8,
                        fontSize: 13,
                        color: "#333",
                        background: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      <option value={1}>1 km</option>
                      <option value={2}>2 km</option>
                      <option value={5}>5 km</option>
                      <option value={10}>10 km</option>
                      <option value={20}>20 km</option>
                    </select>
                  </div>

                  {/* Google Maps container */}
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      height: isMobile ? 300 : 420,
                      borderRadius: 12,
                      overflow: "hidden",
                      border: "1.5px solid #e8e8e8",
                      marginBottom: 10,
                    }}
                  >
                    <div
                      ref={mapRef}
                      style={{ width: "100%", height: "100%" }}
                    />

                    {/* Loading overlay — igual ao styles.loader do Maps.tsx */}
                    {mapLoading && (
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background: "rgba(255,255,255,0.7)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 10,
                        }}
                      >
                        <CircularProgress
                          style={{ color: "#00b6bc" }}
                          size={28}
                        />
                      </div>
                    )}

                    {/* "Pesquisar nesta área" — igual ao refreshButton do Maps.tsx */}
                    {movLoading && !mapLoading && (
                      <button
                        onClick={() => {
                          setMovLoading(false);
                          scanPin(
                            mapCenter[0],
                            mapCenter[1],
                            mapRadius,
                            mapSearch,
                          );
                        }}
                        style={{
                          position: "absolute",
                          top: 12,
                          left: "50%",
                          transform: "translateX(-50%)",
                          background: "#00b6bc",
                          color: "#fff",
                          border: "none",
                          borderRadius: 20,
                          padding: "8px 18px",
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: "pointer",
                          zIndex: 10,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("Pesquisar nesta área")}
                      </button>
                    )}
                  </div>

                  {/* List of nearby establishments (also clickable) */}
                  {mapEstabs.length > 0 && (
                    <div
                      style={{
                        maxHeight: 180,
                        overflowY: "auto",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                      }}
                    >
                      {mapEstabs.map((e) => (
                        <div
                          key={e.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "10px 14px",
                            cursor: "pointer",
                            borderBottom: "1px solid #f7f7f7",
                          }}
                          onClick={() => addEstab(e)}
                          onMouseEnter={(el) =>
                            (el.currentTarget.style.background = "#f9f9f9")
                          }
                          onMouseLeave={(el) =>
                            (el.currentTarget.style.background = "#fff")
                          }
                        >
                          <span style={{ color: "#00b6bc", flexShrink: 0 }}>
                            <IconStore />
                          </span>
                          <div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#111",
                              }}
                            >
                              {e.name}
                            </div>
                            {e.addressInGoogleFormat && (
                              <div style={{ fontSize: 11, color: "#aaa" }}>
                                {e.addressInGoogleFormat}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {!mapLoading && mapEstabs.length === 0 && (
                    <p
                      style={{
                        fontSize: 12,
                        color: "#aaa",
                        textAlign: "center",
                        margin: "8px 0",
                      }}
                    >
                      {t(
                        "Nenhum estabelecimento encontrado nesta área. Mova o mapa ou aumente o raio.",
                      )}
                    </p>
                  )}

                  <EstabNotFoundHint />
                </div>
              )}

              {/* Selected chips (both modes) */}
              {selected.length > 0 && (
                <div style={group}>
                  <label style={labelStyle}>{t("Selecionados:")}</label>
                  <div style={chips}>
                    {selected.map((e) => (
                      <div key={e.id} style={chip}>
                        <IconStore />
                        <span>{e.name}</span>
                        <button
                          type="button"
                          style={chipRemove}
                          onClick={() => removeEstab(e.id)}
                        >
                          <IconX />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Dados do estabelecimento ───────────────────────────── */}
              <div
                style={{
                  borderTop: "1px solid #f0f0f0",
                  margin: "20px 0 16px",
                }}
              />
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#333",
                  margin: "0 0 14px",
                }}
              >
                {t("Dados do estabelecimento")}
              </p>

              <div
                style={grid2("1fr", "minmax(0, 1fr) minmax(0, 1fr)", 12)}
              >
                <div style={group}>
                  <label style={labelStyle}>{t("Instagram")}</label>
                  <div style={inputWrap}>
                    <span
                      style={{
                        color: "#aaa",
                        fontSize: 14,
                        marginRight: 2,
                        flexShrink: 0,
                      }}
                    >
                      @
                    </span>
                    <input
                      style={inputStyle}
                      type="text"
                      placeholder={t("seu_perfil")}
                      value={instagram}
                      onChange={(e) =>
                        setInstagram(e.target.value.replace(/^@/, ""))
                      }
                      onFocus={focusBorder}
                      onBlur={blurBorder}
                      maxLength={100}
                    />
                  </div>
                </div>

                <div style={group}>
                  <label style={labelStyle}>{t("Site")}</label>
                  <div style={inputWrap}>
                    <input
                      style={inputStyle}
                      type="url"
                      placeholder={t("www.exemplo.com.br")}
                      value={site}
                      onChange={(e) => setSite(e.target.value)}
                      onFocus={focusBorder}
                      onBlur={blurBorder}
                      maxLength={255}
                    />
                  </div>
                </div>
              </div>

              {/* Estabelecimento já vinculado a outro usuário */}
              {takenEstabName && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    background: "#fff7e6",
                    border: "1px solid #fde68a",
                    borderRadius: 10,
                    padding: "12px 14px",
                    marginTop: 12,
                  }}
                >
                  <span style={{ fontSize: 16, lineHeight: 1 }}>⚠️</span>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 13,
                      color: "#92400e",
                      lineHeight: 1.6,
                    }}
                  >
                    {t(
                      '"{nome}" já está vinculado a outro usuário. Se este estabelecimento é seu,',
                      { nome: takenEstabName },
                    )}{" "}
                    <a
                      href={`mailto:${PLACES_EMAIL}?subject=${encodeURIComponent(
                        t("Estabelecimento já cadastrado — {nome}", {
                          nome: takenEstabName,
                        }),
                      )}&body=${encodeURIComponent(
                        t(
                          'Olá! Tentei cadastrar o estabelecimento "{nome}" mas ele já está vinculado a outro usuário. Sou o responsável por este local e gostaria de regularizar o acesso.',
                          { nome: takenEstabName },
                        ),
                      )}`}
                      style={{
                        color: "#92400e",
                        fontWeight: 700,
                        textDecoration: "underline",
                      }}
                    >
                      {t("fale com o suporte")}
                    </a>
                    .
                  </p>
                </div>
              )}

              {/* Escolha de plano desligada: todo cadastro novo entra no
                  Insight (3 meses grátis, sem cartão) e o upgrade é feito
                  dentro do painel. O bloco segue aqui inteiro, atrás da
                  constante, para religar sem reescrever. */}
              {ESCOLHA_DE_PLANO_NO_CADASTRO && (
                <>
              {/* ── Escolha do plano ─────────────────────────────────────── */}
              <div
                style={{
                  borderTop: "1px solid #f0f0f0",
                  margin: "24px 0 18px",
                }}
              />

              <p
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#111",
                  margin: "0 0 4px",
                }}
              >
                {t("Qual plano combina com você?")}
              </p>
              <p
                style={{
                  fontSize: 13,
                  color: "#888",
                  margin: "0 0 16px",
                  lineHeight: 1.5,
                }}
              >
                {t(
                  "Comece pelo que fizer sentido agora — dá para trocar quando quiser, na área de Assinatura.",
                )}
              </p>

              {/* Faixa rolável: 4 cards verticais lado a lado */}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  overflowX: "auto",
                  paddingBottom: 10,
                  marginBottom: 4,
                  scrollbarWidth: "thin",
                }}
              >
                {PLAN_OPTIONS.map((opt) => {
                  const active = plan === opt.key;
                  return (
                    <div
                      key={opt.key}
                      onClick={() => setPlan(opt.key)}
                      style={{
                        flex: "0 0 auto",
                        width: 186,
                        minHeight: 290,
                        display: "flex",
                        flexDirection: "column",
                        cursor: "pointer",
                        borderRadius: 14,
                        padding: "18px 16px",
                        border: active
                          ? "2px solid #00b6bc"
                          : "1.5px solid #e8e8e8",
                        background: active ? "#f0fdfe" : "#fff",
                        transition: "border-color .15s, background .15s",
                      }}
                    >
                      {/* Selo */}
                      <span
                        style={{
                          alignSelf: "flex-start",
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: 0.3,
                          textTransform: "uppercase",
                          color: opt.badgeColor,
                          background: opt.badgeBg,
                          borderRadius: 999,
                          padding: "4px 9px",
                          marginBottom: 12,
                        }}
                      >
                        {t(opt.badge)}
                      </span>

                      {/* Nome */}
                      <p
                        style={{
                          margin: "0 0 2px",
                          fontSize: 17,
                          fontWeight: 700,
                          color: "#111",
                        }}
                      >
                        {opt.name}
                      </p>
                      {opt.family && (
                        <p
                          style={{
                            margin: "0 0 8px",
                            fontSize: 11,
                            color: "#aaa",
                            fontWeight: 600,
                          }}
                        >
                          {opt.family}
                        </p>
                      )}

                      {/* Preço */}
                      <div style={{ marginBottom: 12 }}>
                        <span
                          style={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: "#111",
                          }}
                        >
                          {t(opt.price)}
                        </span>
                        <span style={{ fontSize: 12, color: "#999" }}>
                          {t(opt.period)}
                        </span>
                      </div>

                      {/* Vantagens */}
                      <ul
                        style={{
                          margin: 0,
                          padding: 0,
                          listStyle: "none",
                          flex: 1,
                        }}
                      >
                        {opt.features.map((f) => (
                          <li
                            key={f}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 6,
                              fontSize: 12,
                              color: "#555",
                              lineHeight: 1.5,
                              marginBottom: 7,
                            }}
                          >
                            <span
                              style={{
                                color: "#00b6bc",
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              ·
                            </span>
                            {t(f)}
                          </li>
                        ))}
                      </ul>

                      {/* Marcador de seleção */}
                      <div
                        style={{
                          marginTop: 12,
                          textAlign: "center",
                          fontSize: 12,
                          fontWeight: 700,
                          color: active ? "#00b6bc" : "#bbb",
                        }}
                      >
                        {active ? t("✓ Selecionado") : t("Selecionar")}
                      </div>
                    </div>
                  );
                })}
              </div>

              <p style={{ fontSize: 11, color: "#bbb", margin: "0 0 4px" }}>
                {t("Arraste para o lado para ver todos os planos")}
              </p>
                </>
              )}

              {step2Errors.estabilishments && !step2Errors.cnpj && (
                <p style={{ ...errorStyle, marginBottom: 12 }}>
                  {t(step2Errors.estabilishments)}
                </p>
              )}

              {/* O envio acontece aqui quando as etapas de verificação estão
                  desligadas, então o erro e o progresso precisam aparecer
                  nesta tela — antes só existiam no passo 4. */}
              {!ETAPAS_DE_VERIFICACAO && submitError && (
                <p
                  style={{
                    ...errorStyle,
                    marginBottom: 12,
                    whiteSpace: "pre-line",
                  }}
                >
                  {t(submitError)}
                </p>
              )}

              {/* ── Ações ────────────────────────────────────────────────── */}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  marginTop: 24,
                  paddingTop: 18,
                  borderTop: "1px solid #f0f0f0",
                }}
              >
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{
                    flex: "0 0 auto",
                    padding: "12px 26px",
                    background: "transparent",
                    color: "#888",
                    border: "1.5px solid #e0e0e0",
                    borderRadius: 10,
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                >
                  ← {t("Voltar")}
                </button>

                <button
                  type="submit"
                  disabled={selected.length === 0 || submitLoading}
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    padding: "12px 24px",
                    background: "#00b6bc",
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: 14,
                    cursor:
                      selected.length === 0 || submitLoading
                        ? "not-allowed"
                        : "pointer",
                    opacity: selected.length === 0 || submitLoading ? 0.5 : 1,
                  }}
                >
                  {ETAPAS_DE_VERIFICACAO ? (
                    <>{t("Continuar")} →</>
                  ) : submitLoading ? (
                    <>
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                      {t("Criando sua conta...")}
                    </>
                  ) : (
                    t("Criar minha conta")
                  )}
                </button>
              </div>
            </form>
          </>
        )}

        {/* ── STEP 3: Documentos ──────────────────────────────────────── */}
        {step === 3 && (
          <>
            <StepIndicator current={3} />
            <h2 style={title}>{t("Documentos")}</h2>
            <p style={subtitle}>
              {t(
                "Envie os documentos abaixo para que possamos validar seu cadastro.",
              )}
            </p>

            {/* Documentos obrigatórios — deixa claro o que enviar antes do
                upload, evitando idas e voltas na análise. */}
            <div
              style={{
                background: "#fafafa",
                border: "1px solid #eee",
                borderRadius: 12,
                padding: "16px 18px",
                marginBottom: 20,
              }}
            >
              <p
                style={{
                  margin: "0 0 6px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#111",
                }}
              >
                {t("Dados e documentos da empresa (pessoa jurídica)")}
              </p>
              <ul
                style={{
                  margin: "0 0 16px",
                  paddingLeft: 18,
                  fontSize: 13,
                  color: "#555",
                  lineHeight: 1.7,
                }}
              >
                <li>
                  <strong>{t("Cartão CNPJ:")}</strong>{" "}
                  {t(
                    "comprova a inscrição e a situação cadastral ativa perante a Receita Federal.",
                  )}
                </li>
              </ul>

              <p
                style={{
                  margin: "0 0 6px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#111",
                }}
              >
                {t("Documentos do responsável pela abertura da conta")}
              </p>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  fontSize: 13,
                  color: "#555",
                  lineHeight: 1.7,
                }}
              >
                <li>
                  <strong>{t("Documento de identificação:")}</strong>{" "}
                  {t("RG, CNH ou passaporte válido.")}
                </li>
              </ul>
            </div>

            <form onSubmit={handleFinalSubmit} noValidate>
              <div style={group}>
                <label style={labelStyle}>
                  {t("Arquivos")} <span style={required}>*</span>
                </label>

                {/* Drop zone */}
                <div
                  style={dropZone(isDragging)}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  <span style={{ color: "#00b6bc" }}>
                    <IconUpload />
                  </span>
                  <span
                    style={{ fontSize: 14, fontWeight: 600, color: "#333" }}
                  >
                    {t("Clique ou arraste os arquivos aqui")}
                  </span>
                  <span style={{ fontSize: 12, color: "#aaa" }}>
                    {t("PDF, JPG, PNG — múltiplos arquivos permitidos")}
                  </span>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  onChange={(e) => addFiles(e.target.files)}
                />

              </div>

              {/* Selected files */}
              {files.length > 0 && (
                <div
                  style={{
                    ...group,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  {files.map((file, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 14px",
                        background: "#f9f9f9",
                        borderRadius: 10,
                        border: "1px solid #f0f0f0",
                      }}
                    >
                      <span style={{ color: "#00b6bc", flexShrink: 0 }}>
                        <IconFile />
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#111",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.name}
                        </div>
                        <div style={{ fontSize: 11, color: "#aaa" }}>
                          {fmtBytes(file.size)}
                        </div>
                      </div>
                      <button
                        type="button"
                        style={chipRemove}
                        onClick={() => removeFile(idx)}
                      >
                        <IconX />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {submitError && (
                <p
                  style={{
                    ...errorStyle,
                    marginBottom: 12,
                    whiteSpace: "pre-line",
                  }}
                >
                  {t(submitError)}
                </p>
              )}

              {submitLoading && uploadProgress && (
                <p
                  style={{
                    fontSize: 13,
                    color: "#00b6bc",
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <CircularProgress size={13} style={{ color: "#00b6bc" }} />
                  {t(uploadProgress)}
                </p>
              )}

              <div style={actions}>
                <button
                  type="button"
                  style={btnSecondary}
                  onClick={() => setStep(2)}
                >
                  ← {t("Voltar")}
                </button>
                <button
                  type="button"
                  style={files.length === 0 ? btnDisabled : btnPrimary}
                  disabled={files.length === 0}
                  onClick={() => {
                    setSubmitError("");
                    setStep(4);
                  }}
                >
                  {t("Continuar")} →
                </button>
              </div>
            </form>
          </>
        )}

        {/* ── STEP 4: Selfie com documento ────────────────────────────── */}
        {step === 4 && (
          <>
            <StepIndicator current={4} />
            <h2 style={title}>{t("Selfie com documento")}</h2>
            <p style={subtitle}>
              {t(
                "Envie uma foto sua segurando seu documento de identificação (RG ou CNH). Usamos essa imagem apenas para confirmar sua identidade.",
              )}
            </p>

            <form onSubmit={handleFinalSubmit} noValidate>
              {/* Orientações */}
              <div
                style={{
                  background: "#e6f9fa",
                  borderRadius: 10,
                  padding: "14px 16px",
                  marginBottom: 18,
                  fontSize: 13,
                  color: "#0b6f73",
                  lineHeight: 1.7,
                }}
              >
                <strong style={{ display: "block", marginBottom: 6 }}>
                  {t("Para a foto ser aprovada:")}
                </strong>
                ·{" "}
                {t(
                  "Seu rosto e o documento devem aparecer juntos, na mesma foto",
                )}
                <br />· {t("Os dados do documento precisam estar legíveis")}
                <br />· {t("Evite reflexos, sombras fortes e imagens tremidas")}
                <br />·{" "}
                {t(
                  "Não cubra o rosto nem os dados com os dedos . Não usar boné, óculos ou nenhum outro tipo de acessório.",
                )}
              </div>

              {/* Upload / preview */}
              {cameraOpen ? (
                /* ── Câmera aberta ─────────────────────────────────────── */
                <div style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      position: "relative",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#000",
                      border: "1.5px solid #e8e8e8",
                    }}
                  >
                    <video
                      ref={videoRef}
                      playsInline
                      muted
                      style={{
                        width: "100%",
                        maxHeight: 340,
                        objectFit: "cover",
                        display: "block",
                        // espelhado para funcionar como um espelho
                        transform: "scaleX(-1)",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: 10,
                        left: 0,
                        right: 0,
                        textAlign: "center",
                        color: "#fff",
                        fontSize: 12,
                        textShadow: "0 1px 3px rgba(0,0,0,.6)",
                      }}
                    >
                      {t("Enquadre seu rosto e o documento juntos")}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                    <button
                      type="button"
                      onClick={stopCamera}
                      style={{
                        flex: "0 0 auto",
                        padding: "11px 20px",
                        background: "#fff",
                        color: "#888",
                        border: "1.5px solid #e0e0e0",
                        borderRadius: 10,
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      {t("Cancelar")}
                    </button>
                    <button
                      type="button"
                      onClick={capturePhoto}
                      style={{
                        flex: 1,
                        padding: "11px 20px",
                        background: "#00b6bc",
                        color: "#fff",
                        border: "none",
                        borderRadius: 10,
                        fontWeight: 700,
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      {t("📸 Capturar foto")}
                    </button>
                  </div>
                </div>
              ) : !selfie ? (
                /* ── Escolha do método ─────────────────────────────────── */
                <div style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      border: "2px dashed #d5d5d5",
                      borderRadius: 12,
                      padding: "28px 20px",
                      textAlign: "center",
                      background: "#fafafa",
                    }}
                  >
                    <div style={{ fontSize: 32, marginBottom: 10 }}>🤳</div>

                    <button
                      type="button"
                      onClick={openCamera}
                      style={{
                        padding: "11px 26px",
                        background: "#00b6bc",
                        color: "#fff",
                        border: "none",
                        borderRadius: 10,
                        fontWeight: 700,
                        fontSize: 14,
                        cursor: "pointer",
                        marginBottom: 12,
                      }}
                    >
                      {t("Tirar foto agora")}
                    </button>

                    <p
                      style={{
                        margin: "0 0 10px",
                        fontSize: 12,
                        color: "#aaa",
                      }}
                    >
                      {t("ou")}
                    </p>

                    <label
                      htmlFor="selfie-input"
                      style={{
                        display: "inline-block",
                        padding: "9px 22px",
                        background: "#fff",
                        color: "#00b6bc",
                        border: "1.5px solid #00b6bc",
                        borderRadius: 10,
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: "pointer",
                      }}
                    >
                      {t("Enviar um arquivo")}
                    </label>

                    <p
                      style={{
                        margin: "12px 0 0",
                        fontSize: 12,
                        color: "#999",
                      }}
                    >
                      {t("JPG ou PNG — no máximo 10 MB")}
                    </p>

                    <input
                      id="selfie-input"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      style={{ display: "none" }}
                      onChange={(ev) => {
                        const f = ev.target.files?.[0];
                        if (!f) return;
                        if (f.size > 10 * 1024 * 1024) {
                          setSubmitError("A selfie deve ter no máximo 10 MB.");
                          return;
                        }
                        setSubmitError("");
                        setCameraError("");
                        setSelfie(f);
                        setSelfiePreview(URL.createObjectURL(f));
                      }}
                    />
                  </div>

                  {cameraError && (
                    <p
                      style={{
                        margin: "10px 0 0",
                        fontSize: 12,
                        color: "#92400e",
                        background: "#fff7e6",
                        border: "1px solid #fde68a",
                        borderRadius: 8,
                        padding: "10px 12px",
                        lineHeight: 1.5,
                      }}
                    >
                      {t(cameraError)}
                    </p>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: 8 }}>
                  {selfiePreview && (
                    <img
                      src={selfiePreview}
                      alt={t("Pré-visualização da selfie")}
                      style={{
                        width: "100%",
                        maxHeight: 260,
                        objectFit: "contain",
                        borderRadius: 12,
                        border: "1.5px solid #e8e8e8",
                        background: "#fafafa",
                      }}
                    />
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginTop: 8,
                      fontSize: 13,
                      color: "#555",
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selfie.name}
                    </span>
                    <div style={{ display: "flex", gap: 14, flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (selfiePreview) URL.revokeObjectURL(selfiePreview);
                          setSelfie(null);
                          setSelfiePreview(null);
                          openCamera();
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#00b6bc",
                          cursor: "pointer",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {t("Refazer foto")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (selfiePreview) URL.revokeObjectURL(selfiePreview);
                          setSelfie(null);
                          setSelfiePreview(null);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#ef4444",
                          cursor: "pointer",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {t("Remover")}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Aviso do que acontece depois */}
              <p
                style={{
                  fontSize: 12,
                  color: "#888",
                  margin: "14px 0 0",
                  lineHeight: 1.6,
                }}
              >
                {plan === "Insight"
                  ? t(
                      "Seus documentos serão analisados em até 72 horas, mas seu painel é liberado imediatamente.",
                    )
                  : t(
                      "Após o envio você será direcionado para cadastrar o cartão. Seus documentos serão analisados em até 72 horas.",
                    )}
              </p>

              {submitError && (
                <p
                  style={{
                    ...errorStyle,
                    marginTop: 12,
                    whiteSpace: "pre-line",
                  }}
                >
                  {t(submitError)}
                </p>
              )}

              {submitLoading && uploadProgress && (
                <p
                  style={{
                    fontSize: 13,
                    color: "#00b6bc",
                    marginTop: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <CircularProgress size={12} style={{ color: "#00b6bc" }} />{" "}
                  {t(uploadProgress)}
                </p>
              )}

              <div style={actions}>
                <button
                  type="button"
                  style={btnSecondary}
                  disabled={submitLoading}
                  onClick={() => {
                    stopCamera();
                    setStep(3);
                  }}
                >
                  ← {t("Voltar")}
                </button>
                <button
                  type="submit"
                  style={submitLoading || !selfie ? btnDisabled : btnPrimary}
                  disabled={submitLoading || !selfie}
                >
                  {submitLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    t("Enviar cadastro")
                  )}
                </button>
              </div>
            </form>
          </>
        )}

        <SuporteWhatsApp passo={step} />
      </div>
    </div>
  );
};